import {
  NodeTagRow,
  getRowsFromTagsFileContent,
} from "@features/administration/products/EditProduct/nodeTagsHelper"
import { useGraph } from "@features/builder/graph/graphAtoms"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import {
  getMatchingGraphTags,
  getMatchingNodeTags,
} from "@features/builder/NodeDrawer/NodeForm/helpers"
import { GraphDetails, GraphTagDto } from "@features/builder/types"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material"
import { useGetNodeTagsQuery } from "@store/services/productsService"
import { useEffect, useMemo, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useDebounceValue } from "usehooks-ts"

type MatchingNodeTagRow = NodeTagRow & { type: "node"; isChecked: boolean }
type MatchingGraphTagRow = GraphTagDto & { type: "graph"; isChecked: boolean }
type MatchingTagRow = MatchingNodeTagRow | MatchingGraphTagRow

const TagsSuggestionsAlert = () => {
  const form = useFormContext<NodeFormValues>()
  const [matchingTags, setMatchingTags] = useState<MatchingTagRow[]>([])
  const { graphId } = useGraph()
  const [organisationKey, divisionKey, productKey] = (graphId ?? "").split("#")

  const { data: getNodeTagsResponse } = useGetNodeTagsQuery(
    {
      key: productKey ?? "",
      orgKey: organisationKey ?? "",
      divKey: divisionKey ?? "",
    },
    {
      skip: !productKey || !organisationKey || !divisionKey,
    },
  )
  const nodeTagsRows = useMemo(
    () => getRowsFromTagsFileContent(getNodeTagsResponse ?? ""),
    [getNodeTagsResponse],
  )

  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = useMemo(() => {
    try {
      return JSON.parse(graphDocumentData.data)
    } catch {
      return null
    }
  }, [graphDocumentData.data])
  const graphTagsRows = useMemo(() => graphData?.GraphTags ?? [], [graphData])

  const appliedNodeTags = useWatch({ control: form.control, name: "tags" })
  const appliedGraphTags = useWatch({ control: form.control, name: "graph_tags" })
  const label = useWatch({ control: form.control, name: "label" }) ?? ""
  const [debouncedLabel, setDebouncedLabel] = useDebounceValue(label, 500)

  useEffect(() => {
    setDebouncedLabel(label)
  }, [label, setDebouncedLabel])

  useEffect(() => {
    // no point of suggestion if there is less than 3 characters in label
    if (debouncedLabel.length < 3) {
      setMatchingTags([])
      return
    }

    const nodeLevelMatches = getMatchingNodeTags(
      debouncedLabel,
      nodeTagsRows,
      appliedNodeTags ?? [],
    )
    const graphLevelMatches = getMatchingGraphTags(
      debouncedLabel,
      graphTagsRows,
      appliedGraphTags ?? [],
    )

    setMatchingTags([
      ...nodeLevelMatches.map((tag) => ({ ...tag, type: "node" as const, isChecked: true })),
      ...graphLevelMatches.map((tag) => ({ ...tag, type: "graph" as const, isChecked: true })),
    ])
  }, [debouncedLabel, nodeTagsRows, graphTagsRows, appliedNodeTags, appliedGraphTags])

  const handleToggle = (index: number) => {
    if (form.formState.disabled) return

    const updatedTags = matchingTags.map((tag, i) => {
      if (i !== index) return tag
      return { ...tag, isChecked: !tag.isChecked }
    })
    setMatchingTags(updatedTags)
  }

  if (matchingTags.length === 0) return

  const handleApply = () => {
    const checkedNodeTags = matchingTags.filter(
      (tag): tag is MatchingNodeTagRow => tag.type === "node" && tag.isChecked,
    )
    const checkedGraphTags = matchingTags.filter(
      (tag): tag is MatchingGraphTagRow => tag.type === "graph" && tag.isChecked,
    )

    if (checkedNodeTags.length) {
      const tagsToApply = checkedNodeTags.map((tag) => ({ tag: tag.Name, value: tag.Value }))
      form.setValue("tags", [...(appliedNodeTags ?? []), ...tagsToApply])
    }

    if (checkedGraphTags.length) {
      const updatedGraphTags = structuredClone(appliedGraphTags ?? [])

      checkedGraphTags.forEach((tag) => {
        const existingGroup = updatedGraphTags.find((group) => group.tag === tag.Name)
        if (existingGroup) {
          if (!existingGroup.values.includes(tag.Value)) existingGroup.values.push(tag.Value)
        } else {
          updatedGraphTags.push({ tag: tag.Name, values: [tag.Value] })
        }
      })
      form.setValue("graph_tags", updatedGraphTags)
    }
  }

  return (
    <Alert severity="info" sx={{ p: 1 }}>
      <Stack>
        <Typography variant="caption">
          We matched this label against existing tags. <br />
          Do you want to apply them?
        </Typography>

        <List dense>
          {matchingTags.map((row, index) => (
            <ListItem key={`${row.type}-${row.Name}-${row.Value}`}>
              <FormControlLabel
                sx={{ m: 0 }}
                label={
                  <Typography
                    variant="body2"
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleToggle(index)}
                  >
                    <Chip
                      label={row.type === "node" ? "Node-level" : "Graph-level"}
                      color={row.type === "node" ? "default" : "info"}
                      size="small"
                      sx={{ mr: 1, height: 16 }}
                    />
                    Tag <strong>{row.Name}</strong> with value <strong>{row.Value}</strong>{" "}
                    {row.Description ? `(${row.Description})` : ""}
                  </Typography>
                }
                control={
                  <Checkbox
                    edge="start"
                    size="small"
                    disabled={form.formState.disabled}
                    checked={row.isChecked}
                    onChange={() => handleToggle(index)}
                  />
                }
              />
            </ListItem>
          ))}
        </List>

        <Box>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={form.formState.disabled}
          >
            Apply
          </Button>
        </Box>
      </Stack>
    </Alert>
  )
}

export default TagsSuggestionsAlert
